import type { Block, Command, OnceBlock, Scenario, StateBlock, TransitionBlock } from './types';

function cleanId(v: unknown): string {
  return String(v || '').trim();
}

function cleanCommand(v: unknown): string {
  return String(v || '').trim();
}

function normalizeLocale(raw: string): string {
  const v = String(raw || '').trim().toLowerCase();
  if (!v) return 'en';
  return v.split('-')[0];
}

// Commands translations are stored globally in i18n store as keys like `cmd.<hash>`.
// Hash must match backend/media-server (FNV-1a 32-bit → base36 over normalized RU).
function normalizeRuCommandText(text: unknown): string {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function fnv1a32(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    // h *= 16777619 (mod 2^32), implemented via shifts to match backend
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

function commandKeyFromRu(text: unknown): string | null {
  const norm = normalizeRuCommandText(text);
  if (!norm) return null;
  return `cmd.${fnv1a32(norm).toString(36)}`;
}

function hasCyrillic(text: unknown): boolean {
  return /[А-Яа-яЁё]/.test(String(text || ''));
}

/**
 * Translate a command text using i18n. Older scenarios may store English
 * source commands, so the current locale decides whether raw text is enough.
 */
export function translateCommandText(
  ruText: string,
  i18nT: ((key: string, fallback?: string) => string) | null,
  locale?: string,
): string {
  if (!ruText) return ruText;
  const loc = normalizeLocale(locale || '');
  const sourceIsRu = hasCyrillic(ruText);
  if (loc === 'ru' && sourceIsRu) return ruText;
  if (loc === 'en' && !sourceIsRu) return ruText;
  const key = commandKeyFromRu(ruText);
  if (!key || !i18nT) return ruText;
  const translated = String(i18nT(key, '') || '').trim();
  return translated || ruText;
}

export type ScenarioIndex = {
  scenario: Scenario;
  statesById: Map<string, StateBlock>;
  transitionsByFrom: Map<string, TransitionBlock[]>;
  oncesByState: Map<string, OnceBlock[]>;
  initialStateId: string | null;
};

export function indexScenario(s: Scenario): ScenarioIndex {
  const blocks: Block[] = Array.isArray(s?.blocks) ? s.blocks : [];
  const statesById = new Map<string, StateBlock>();
  const transitionsByFrom = new Map<string, TransitionBlock[]>();
  const oncesByState = new Map<string, OnceBlock[]>();

  for (const b of blocks) {
    if (!b || typeof b !== 'object') continue;
    const type = (b as any).type;
    if (type === 'state') {
      const id = cleanId((b as any).id);
      if (id) statesById.set(id, b as StateBlock);
    } else if (type === 'transition') {
      const from = cleanId((b as any).from);
      if (!from) continue;
      const arr = transitionsByFrom.get(from) || [];
      arr.push(b as TransitionBlock);
      transitionsByFrom.set(from, arr);
    } else if (type === 'once') {
      const stateId = cleanId((b as any).stateId || (b as any).state || (b as any).from);
      if (!stateId) continue;
      const arr = oncesByState.get(stateId) || [];
      arr.push(b as OnceBlock);
      oncesByState.set(stateId, arr);
    }
  }

  // Contract: the first block is the initial state (legacy agreement), but be safe if first isn't a state.
  const first = blocks[0];
  const initialStateId =
    first && (first as any).type === 'state' && cleanId((first as any).id) ? cleanId((first as any).id) : (statesById.keys().next().value || null);

  return { scenario: s, statesById, transitionsByFrom, oncesByState, initialStateId };
}

export function pickStateVideo(state: StateBlock | null): string | null {
  if (!state) return null;
  const urls = state.videoUrls;
  if (Array.isArray(urls) && urls.length > 0) {
    const i = Math.floor(Math.random() * urls.length);
    const u = String(urls[i] || '').trim();
    return u || null;
  }
  // Legacy fallback: accept videoUrl in older scenarios.
  const u = String(state.videoUrl || '').trim();
  return u || null;
}

export function getTransitionForCommand(idx: ScenarioIndex, fromStateId: string, command: string): TransitionBlock | null {
  const from = cleanId(fromStateId);
  const cmd = String(command || '');
  const arr = idx.transitionsByFrom.get(from) || [];
  return arr.find((t) => String(t?.command || '') === cmd) || null;
}

export function getOnceForCommand(idx: ScenarioIndex, stateId: string, command: string): OnceBlock | null {
  const st = cleanId(stateId);
  const cmd = String(command || '');
  const arr = idx.oncesByState.get(st) || [];
  return arr.find((o) => String(o?.command || '') === cmd) || null;
}

export function labelForBlock(
  block: { command?: string; label?: string } | null,
  locale: string,
  i18nT?: (key: string, fallback?: string) => string,
): string {
  if (!block) return '';
  const commandRu = normalizeRuCommandText(block.command);
  const key = commandRu ? commandKeyFromRu(commandRu) : null;
  const loc = normalizeLocale(locale);
  const sourceIsRu = hasCyrillic(commandRu);

  if (loc === 'ru' && sourceIsRu) {
    return String(block.command || block.label || '').trim();
  }
  if (loc === 'en' && commandRu && !sourceIsRu) return commandRu;

  // Prefer global commands i18n store (cmd.<hash>) when available.
  if (i18nT && key) {
    const translated = String(i18nT(key, '') || '').trim();
    if (translated) return translated;
  }

  // Fallback: scenario label/command (should typically be RU).
  return String(block.label || block.command || '').trim();
}

export function availableCommands(
  idx: ScenarioIndex,
  stateId: string,
  locale: string,
  i18nT?: (key: string, fallback?: string) => string,
): Command[] {
  const st = cleanId(stateId);
  const out: Command[] = [];

  const transitions = idx.transitionsByFrom.get(st) || [];
  for (const t of transitions) {
    if (!t?.command || t.command === 'auto') continue;
    const hasVideo =
        (Array.isArray(t.videoUrls) && t.videoUrls.some((u) => u && String(u).trim() !== '')) ||
        (t.videoUrl && String(t.videoUrl).trim() !== '');
    if (!hasVideo) continue;
    const cmd = cleanCommand(t.command);
    if (!cmd) continue;
    out.push({
      kind: 'transition',
      command: cmd,
      label: labelForBlock(t, locale, i18nT) || cmd,
      cost: Number(t.cost || 0),
      available: true,
      premiumOnly: Boolean((t as any).premiumOnly),
    });
  }

  const onces = idx.oncesByState.get(st) || [];
  for (const o of onces) {
    if (!o?.command) continue;
    const hasVideo =
      (Array.isArray((o as any).videoUrls) && (o as any).videoUrls.some((u: any) => u && String(u).trim() !== '')) ||
      (o.videoUrl && String(o.videoUrl).trim() !== '');
    if (!hasVideo) continue;
    const cmd = cleanCommand(o.command);
    if (!cmd) continue;
    out.push({
      kind: 'once',
      command: cmd,
      label: labelForBlock(o, locale, i18nT) || cmd,
      cost: Number(o.cost || 0),
      available: true,
      premiumOnly: Boolean((o as any).premiumOnly),
    });
  }

  return out;
}

export function allCommands(
  idx: ScenarioIndex,
  locale: string,
  i18nT?: (key: string, fallback?: string) => string,
): Omit<Command, 'available'>[] {
  const map = new Map<string, { kind: 'transition' | 'once'; command: string; label: string; cost: number; premiumOnly?: boolean }>();

  // transitions
  idx.transitionsByFrom.forEach((arr) => {
    (arr || []).forEach((t) => {
      if (!t?.command || t.command === 'auto') return;
      const hasVideo =
        (Array.isArray(t.videoUrls) && t.videoUrls.some((u) => u && String(u).trim() !== '')) ||
        (t.videoUrl && String(t.videoUrl).trim() !== '');
      if (!hasVideo) return;
      const cmd = cleanCommand(t.command);
      if (!cmd) return;
      const key = `transition:${cmd}`;
      const label = labelForBlock(t, locale, i18nT) || cmd;
      const cost = Number(t.cost || 0);
      const premiumOnly = Boolean((t as any).premiumOnly);
      const prev = map.get(key);
      if (!prev) map.set(key, { kind: 'transition', command: cmd, label, cost, premiumOnly });
      else map.set(key, { ...prev, cost: Math.max(prev.cost, cost), label: prev.label || label, premiumOnly: prev.premiumOnly || premiumOnly });
    });
  });

  // once
  idx.oncesByState.forEach((arr) => {
    (arr || []).forEach((o) => {
      if (!o?.command) return;
      const hasVideo =
        (Array.isArray((o as any).videoUrls) && (o as any).videoUrls.some((u: any) => u && String(u).trim() !== '')) ||
        (o.videoUrl && String(o.videoUrl).trim() !== '');
      if (!hasVideo) return;
      const cmd = cleanCommand(o.command);
      if (!cmd) return;
      const key = `once:${cmd}`;
      const label = labelForBlock(o, locale, i18nT) || cmd;
      const cost = Number(o.cost || 0);
      const premiumOnly = Boolean((o as any).premiumOnly);
      const prev = map.get(key);
      if (!prev) map.set(key, { kind: 'once', command: cmd, label, cost, premiumOnly });
      else map.set(key, { ...prev, cost: Math.max(prev.cost, cost), label: prev.label || label, premiumOnly: prev.premiumOnly || premiumOnly });
    });
  });

  return Array.from(map.values());
}
